/**
 * SWARM BODY — the mass that IS the Stage 1 boss.
 * Spec: docs/BOSS-SWARM-MOTHER.md.
 *
 * THE PREMISE. There is no chassis. Every surface of the Mother is a live
 * drone holding station, and shooting it does not chip armour — it kills
 * drones. The hole that leaves is a real hole the player can fly into.
 * Damage is SPATIAL, not numeric.
 *
 * Four rules from the spec are enforced here rather than trusted to tuning:
 *   1. No health bar drives the kill. This class exposes integrity as
 *      information only; the Mother dies when her core dies.
 *   2. Every drone killed is a drone gone. One drone, one death, one hole.
 *   3. The hole persists. A dead slot stays dead for REGEN_DELAY at minimum,
 *      and holes survive formation changes — cut the wall's left flank and
 *      the eagle is missing a wing.
 *   4. It heals. Replacements fly in continuously, so the player must cut
 *      faster than the body knits.
 *
 * PERFORMANCE. 1,200 drones inside a 16.6 ms budget that already carries the
 * prop field, the quilt and the bullet systems. Three things make that hold:
 * struct-of-arrays storage, ONE instanced draw for the whole body, and a
 * uniform spatial grid for collision — the same grid pattern already proven in
 * EnemySystem's boids. Player fire against 1,200 drones is not tractable with
 * a nested loop and must never be attempted with one.
 */

import * as THREE from 'three'
import { FORMATIONS, type FormationName } from './SwarmFormations'

export const SWARM = {
  /** Enough to read as a mass; still one instanced draw. */
  count: 1200,
  /** Two player rounds at tier 1, one at tier 4+. */
  droneHp: 2,
  /** Collision radius, both against player fire and against the player. */
  droneRadius: 0.34,
  /**
   * RENDERED radius, deliberately smaller than the collision radius.
   *
   * Formations pack members about 0.56 apart, so drawing them at the 0.34
   * collision radius made every neighbour overlap and the body rendered as one
   * solid silhouette — a red blob, not ten thousand things pretending to be
   * one thing. Drawing smaller than you collide also makes the fight FEEL
   * generous: shots land slightly wider than they look.
   */
  droneVisualRadius: 0.19,
  /** Seconds a hole is guaranteed to stay open before that slot can refill. */
  regenDelay: 2.2,
  /** Slots refilled per second once eligible. */
  regenRate: 14,
  /** Seconds to fly from one formation to the next. */
  morphTime: 1.6,
  /** Grid cell edge for collision, in world units. */
  cellSize: 1.2,
} as const

/** Where the body sits in the playfield while it fights. */
const BODY_X = 0
const BODY_Y = 11

export class SwarmBody {
  readonly group = new THREE.Group()

  /* ── Struct-of-arrays. No per-drone objects in the hot loop. ───────────── */
  private readonly alive = new Uint8Array(SWARM.count)
  private readonly hp = new Float32Array(SWARM.count)
  /** Seconds since this slot died; < 0 while alive. */
  private readonly deadFor = new Float32Array(SWARM.count)
  /** Live world position. */
  private readonly x = new Float32Array(SWARM.count)
  private readonly y = new Float32Array(SWARM.count)
  /** Station in the formation being left, and the one being flown to. */
  private readonly fromX = new Float32Array(SWARM.count)
  private readonly fromY = new Float32Array(SWARM.count)
  private readonly toX = new Float32Array(SWARM.count)
  private readonly toY = new Float32Array(SWARM.count)

  /* ── Collision grid, rebuilt each frame. ───────────────────────────────── */
  private readonly cellOf = new Int32Array(SWARM.count)
  private readonly cellStart = new Int32Array(4096 + 1)
  private readonly cellCount = new Int32Array(4096)
  private readonly sorted = new Int32Array(SWARM.count)

  private readonly hull: THREE.InstancedMesh
  private readonly dummy = new THREE.Object3D()

  private formation: FormationName = 'wall'
  /** 0..1 across a morph; 1 means settled. */
  private morph = 1
  private time = 0
  private regenCredit = 0
  /** Advances per volley so emitter selection rotates through the body. */
  private volley = 0
  private aliveCount: number = SWARM.count

  constructor() {
    this.group.name = 'SwarmMother'

    // A drone is ~0.6 u on screen. Silhouette is the entire read, so the
    // geometry is deliberately tiny — 1,200 x a detailed model would be half a
    // million triangles for shapes six pixels across.
    const geo = new THREE.OctahedronGeometry(SWARM.droneVisualRadius, 0)
    geo.scale(1, 1, 0.5)
    // Dark hull, small hot core. The emissive was strong enough that bloom
    // fused the whole mass into one flat red shape; the drones have to read as
    // separate objects or the entire premise is invisible.
    const mat = new THREE.MeshStandardMaterial({
      color: '#0f1216',
      roughness: 0.42,
      metalness: 0.85,
      emissive: '#ff3c0a',
      emissiveIntensity: 0.1,
    })
    this.hull = new THREE.InstancedMesh(geo, mat, SWARM.count)
    this.hull.frustumCulled = false
    this.hull.castShadow = false
    this.group.add(this.hull)

    this.reset()
  }

  get integrity(): number {
    return this.aliveCount / SWARM.count
  }

  get living(): number {
    return this.aliveCount
  }

  get morphing(): boolean {
    return this.morph < 1
  }

  /** Seconds of SIMULATION time since reset. Verification must measure in
   *  this, not wall-clock: the sim is fixed-timestep, so under a slow
   *  renderer it advances far more slowly than real time. */
  get simTime(): number {
    return this.time
  }

  /**
   * Begin flying to a new formation.
   *
   * Slot identity is PRESERVED: drone n holds slot n in every formation, so it
   * travels one continuous path from its old station to its new one.
   * Reassigning slots would produce a swirl of crossing traffic that reads as
   * noise; preserving them produces the shape turning into the next shape,
   * which is the whole effect.
   */
  setFormation(name: FormationName): void {
    if (name === this.formation && this.morph >= 1) return
    const fn = FORMATIONS[name]
    for (let i = 0; i < SWARM.count; i += 1) {
      // Fly from where the drone actually IS, not from its old station — a
      // drone interrupted mid-morph would otherwise snap backwards.
      this.fromX[i] = this.x[i]
      this.fromY[i] = this.y[i]
      const p = fn(i, SWARM.count)
      this.toX[i] = p.x
      this.toY[i] = p.y
    }
    this.formation = name
    this.morph = 0
  }

  update(dt: number): void {
    this.time += dt
    if (this.morph < 1) {
      this.morph = Math.min(1, this.morph + dt / SWARM.morphTime)
    }
    this.regen(dt)

    const m = this.morph
    for (let i = 0; i < SWARM.count; i += 1) {
      if (!this.alive[i]) {
        if (this.deadFor[i] >= 0) this.deadFor[i] += dt
        continue
      }
      // Stagger the start per slot so the body flows into its new shape
      // instead of every member setting off on the same frame.
      const stagger = (i % 37) / 37 * 0.28
      const t = m <= stagger ? 0 : Math.min(1, (m - stagger) / (1 - stagger))
      const e = t * t * (3 - 2 * t) // smoothstep

      const bx = this.fromX[i] + (this.toX[i] - this.fromX[i]) * e
      const by = this.fromY[i] + (this.toY[i] - this.fromY[i]) * e

      // Idle breathing, out of phase per drone. A perfectly rigid mass reads
      // as geometry; a drifting one reads as ten thousand things holding
      // station, which is the entire point.
      const ph = i * 0.7139
      this.x[i] = BODY_X + bx + Math.sin(this.time * 1.7 + ph) * 0.13
      this.y[i] = BODY_Y + by + Math.cos(this.time * 1.3 + ph * 1.7) * 0.11
    }

    this.buildGrid()
    this.sync()
  }

  /**
   * Pick living drones spread across the body, to fire FROM.
   *
   * The Mother has no guns; her drones are the guns. Every emitter is a real
   * member at its real position, which is what makes cutting the left flank
   * genuinely silence the left flank — progress the player feels rather than
   * reads off a bar.
   *
   * Sampling strides through slot order rather than taking the first N, so the
   * emitters spread across the whole shape, and dead slots are skipped — a
   * destroyed section simply contributes no shooters.
   */
  sampleLiving(want: number, out: Array<{ x: number; y: number }>): number {
    out.length = 0
    if (this.aliveCount === 0 || want <= 0) return 0
    const stride = Math.max(1, Math.floor(SWARM.count / want))
    // Offset the walk each call so successive volleys do not always leave the
    // same members, which would read as fixed turret positions.
    const start = (this.volley * 7919) % SWARM.count
    for (let k = 0; k < SWARM.count && out.length < want; k += stride) {
      const i = (start + k) % SWARM.count
      if (!this.alive[i]) continue
      out.push({ x: this.x[i], y: this.y[i] })
    }
    this.volley += 1
    return out.length
  }

  /**
   * The lowest living drone near a given x — the member that would actually
   * have line of fire downward. Returns null if that column is cut open.
   */
  lowestAt(wx: number, tolerance: number): { x: number; y: number } | null {
    let best: { x: number; y: number } | null = null
    for (let i = 0; i < SWARM.count; i += 1) {
      if (!this.alive[i]) continue
      if (Math.abs(this.x[i] - wx) > tolerance) continue
      if (!best || this.y[i] < best.y) best = { x: this.x[i], y: this.y[i] }
    }
    return best
  }

  /**
   * Player fire. Returns how many drones died, so the caller can score and
   * spark. Damage is applied to EVERY drone in the radius, not the first —
   * a hole is made by clearing an area, not by chipping one member.
   */
  damage(wx: number, wy: number, radius: number, amount: number): number {
    let killed = 0
    const r = radius + SWARM.droneRadius
    const rr = r * r
    this.forEachNear(wx, wy, r, (i) => {
      // The grid is rebuilt once per frame, so it can still list drones that
      // died earlier in THIS frame. Without this guard a dead drone would be
      // killed twice (double-decrementing the live count) and would keep
      // reporting as solid until the next rebuild.
      if (!this.alive[i]) return
      const dx = this.x[i] - wx
      const dy = this.y[i] - wy
      if (dx * dx + dy * dy > rr) return
      this.hp[i] -= amount
      if (this.hp[i] > 0) return
      this.alive[i] = 0
      this.deadFor[i] = 0
      this.aliveCount -= 1
      killed += 1
    })
    return killed
  }

  /** True if a drone occupies this point — the player's collision test. */
  hits(wx: number, wy: number, radius: number): boolean {
    const r = radius + SWARM.droneRadius
    const rr = r * r
    let hit = false
    this.forEachNear(wx, wy, r, (i) => {
      if (hit || !this.alive[i]) return
      const dx = this.x[i] - wx
      const dy = this.y[i] - wy
      if (dx * dx + dy * dy <= rr) hit = true
    })
    return hit
  }

  /**
   * Is there a clear corridor from the player to this point?
   *
   * This is what proves rule 3 — the hole has to be REAL. The core is only
   * reachable when the player has actually cut a tunnel to it, so line of
   * sight is sampled through the body rather than assumed.
   */
  clearLineTo(fromX: number, fromY: number, toX: number, toY: number): boolean {
    const dx = toX - fromX
    const dy = toY - fromY
    const dist = Math.hypot(dx, dy)
    const steps = Math.max(2, Math.ceil(dist / 0.8))
    for (let s = 1; s <= steps; s += 1) {
      const t = s / steps
      if (this.hits(fromX + dx * t, fromY + dy * t, 0.1)) return false
    }
    return true
  }

  /**
   * Refill dead slots.
   *
   * Holes must persist long enough to matter, so a slot cannot refill until
   * REGEN_DELAY has passed. After that the Mother knits at a fixed rate and
   * the player has to out-cut it — that race is what turns excavation from a
   * chore into a fight.
   */
  private regen(dt: number): void {
    if (this.aliveCount >= SWARM.count) return
    this.regenCredit += SWARM.regenRate * dt
    while (this.regenCredit >= 1) {
      let filled = false
      for (let i = 0; i < SWARM.count; i += 1) {
        if (this.alive[i] || this.deadFor[i] < SWARM.regenDelay) continue
        const p = FORMATIONS[this.formation](i, SWARM.count)
        this.alive[i] = 1
        this.hp[i] = SWARM.droneHp
        this.deadFor[i] = -1
        this.toX[i] = p.x
        this.toY[i] = p.y
        // Replacements fly IN from above rather than appearing. A drone that
        // pops into existence tells the player their work was never real.
        this.fromX[i] = p.x
        this.fromY[i] = p.y + 26
        this.x[i] = BODY_X + this.fromX[i]
        this.y[i] = BODY_Y + this.fromY[i]
        this.aliveCount += 1
        filled = true
        break
      }
      this.regenCredit -= 1
      if (!filled) {
        this.regenCredit = 0
        return
      }
    }
  }

  /* ── Uniform spatial grid. O(n), never O(n^2). ─────────────────────────── */

  private gridMinX = 0
  private gridMinY = 0
  private gridCols = 1
  private gridRows = 1

  private buildGrid(): void {
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    for (let i = 0; i < SWARM.count; i += 1) {
      if (!this.alive[i]) continue
      if (this.x[i] < minX) minX = this.x[i]
      if (this.x[i] > maxX) maxX = this.x[i]
      if (this.y[i] < minY) minY = this.y[i]
      if (this.y[i] > maxY) maxY = this.y[i]
    }
    if (minX === Infinity) {
      this.gridCols = 0
      return
    }
    this.gridMinX = minX
    this.gridMinY = minY
    this.gridCols = Math.max(1, Math.min(64, Math.ceil((maxX - minX) / SWARM.cellSize) + 1))
    this.gridRows = Math.max(1, Math.min(64, Math.ceil((maxY - minY) / SWARM.cellSize) + 1))

    const cells = this.gridCols * this.gridRows
    this.cellCount.fill(0, 0, cells)
    for (let i = 0; i < SWARM.count; i += 1) {
      if (!this.alive[i]) {
        this.cellOf[i] = -1
        continue
      }
      const c = this.cellIndex(this.x[i], this.y[i])
      this.cellOf[i] = c
      this.cellCount[c] += 1
    }
    // Prefix sum into cellStart, then scatter — a counting sort, so the
    // per-cell member lists are contiguous and cache-friendly.
    let running = 0
    for (let c = 0; c < cells; c += 1) {
      this.cellStart[c] = running
      running += this.cellCount[c]
    }
    this.cellStart[cells] = running
    const cursor = this.cellCount // reuse as write cursor
    for (let c = 0; c < cells; c += 1) cursor[c] = this.cellStart[c]
    for (let i = 0; i < SWARM.count; i += 1) {
      const c = this.cellOf[i]
      if (c < 0) continue
      this.sorted[cursor[c]] = i
      cursor[c] += 1
    }
  }

  private cellIndex(wx: number, wy: number): number {
    const cx = Math.max(
      0,
      Math.min(this.gridCols - 1, Math.floor((wx - this.gridMinX) / SWARM.cellSize)),
    )
    const cy = Math.max(
      0,
      Math.min(this.gridRows - 1, Math.floor((wy - this.gridMinY) / SWARM.cellSize)),
    )
    return cy * this.gridCols + cx
  }

  private forEachNear(wx: number, wy: number, r: number, fn: (i: number) => void): void {
    if (this.gridCols === 0) return
    const span = Math.ceil(r / SWARM.cellSize)
    const cx = Math.floor((wx - this.gridMinX) / SWARM.cellSize)
    const cy = Math.floor((wy - this.gridMinY) / SWARM.cellSize)
    for (let gy = cy - span; gy <= cy + span; gy += 1) {
      if (gy < 0 || gy >= this.gridRows) continue
      for (let gx = cx - span; gx <= cx + span; gx += 1) {
        if (gx < 0 || gx >= this.gridCols) continue
        const c = gy * this.gridCols + gx
        const end = this.cellStart[c + 1]
        for (let k = this.cellStart[c]; k < end; k += 1) fn(this.sorted[k])
      }
    }
  }

  private sync(): void {
    let n = 0
    for (let i = 0; i < SWARM.count; i += 1) {
      if (!this.alive[i]) continue
      this.dummy.position.set(this.x[i], this.y[i], 0.4)
      // Slow individual roll so the mass glitters rather than sitting flat.
      this.dummy.rotation.set(0, 0, this.time * 0.9 + i * 0.37)
      this.dummy.scale.setScalar(1)
      this.dummy.updateMatrix()
      this.hull.setMatrixAt(n, this.dummy.matrix)
      n += 1
    }
    this.hull.count = n
    this.hull.instanceMatrix.needsUpdate = true
  }

  reset(): void {
    const fn = FORMATIONS.wall
    for (let i = 0; i < SWARM.count; i += 1) {
      const p = fn(i, SWARM.count)
      this.alive[i] = 1
      this.hp[i] = SWARM.droneHp
      this.deadFor[i] = -1
      this.fromX[i] = p.x
      this.fromY[i] = p.y
      this.toX[i] = p.x
      this.toY[i] = p.y
      this.x[i] = BODY_X + p.x
      this.y[i] = BODY_Y + p.y
    }
    this.aliveCount = SWARM.count
    this.formation = 'wall'
    this.morph = 1
    this.time = 0
    this.regenCredit = 0
    this.buildGrid()
    this.sync()
  }

  dispose(): void {
    this.hull.geometry.dispose()
    ;(this.hull.material as THREE.Material).dispose()
  }
}
